import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { embeddingsService } from '../../../services/embeddingsService';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const DocumentManagementSection = ({ project }) => {
  const { user } = useAuth();
  const [previewDocument, setPreviewDocument] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Load documents from Supabase
  useEffect(() => {
    if (!project?.id) return;

    const loadDocuments = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase?.from('project_documents')?.select('*')?.eq('project_id', project?.id)?.order('created_at', { ascending: false });

        if (error) throw error;

        setDocuments(data?.map(doc => ({
          id: doc?.id,
          name: doc?.name,
          type: doc?.type,
          size: doc?.file_size ? `${(doc?.file_size / 1024 / 1024)?.toFixed(1)} MB` : 'N/A',
          format: doc?.format,
          uploadDate: doc?.created_at,
          version: doc?.metadata?.version || 'v1.0',
          status: doc?.status === 'indexed' ? 'aprobado' : doc?.status === 'processing' ? 'revision' : 'pendiente',
          icon: getIconForType(doc?.type),
          url: doc?.file_url,
          metadata: doc?.metadata
        })) || []);
      } catch (err) {
        setError(err?.message || 'Failed to load documents');
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [project?.id]);

  const getIconForType = (type) => {
    const iconMap = {
      pdd: 'FileText',
      monitoring: 'BarChart3',
      final_report: 'FileCode',
      gee_report: 'Map',
      validation: 'CheckCircle',
      verification: 'Shield',
      methodology: 'BookOpen',
      stakeholder: 'Users',
      other: 'File'
    };
    return iconMap?.[type] || 'File';
  };

  const handleUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !user || !project?.id) return;

    try {
      setUploading(true);
      setError('');

      // Create document record
      const { data: newDoc, error: docError } = await supabase?.from('project_documents')?.insert({
          project_id: project?.id,
          user_id: user?.id,
          name: file?.name,
          type: 'other',
          file_size: file?.size,
          format: file?.name?.split('.')?.pop()?.toUpperCase(),
          status: 'processing',
          metadata: { uploaded_at: new Date()?.toISOString() }
        })?.select()?.single();

      if (docError) throw docError;

      // Read file content
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          setProcessing(true);
          const content = e?.target?.result;

          // Process embeddings
          await embeddingsService?.processDocumentEmbeddings(newDoc?.id, content);

          // Reload documents
          const { data: updatedDocs } = await supabase?.from('project_documents')?.select('*')?.eq('project_id', project?.id)?.order('created_at', { ascending: false });

          setDocuments(updatedDocs?.map(doc => ({
            id: doc?.id,
            name: doc?.name,
            type: doc?.type,
            size: doc?.file_size ? `${(doc?.file_size / 1024 / 1024)?.toFixed(1)} MB` : 'N/A',
            format: doc?.format,
            uploadDate: doc?.created_at,
            version: doc?.metadata?.version || 'v1.0',
            status: doc?.status === 'indexed' ? 'aprobado' : doc?.status === 'processing' ? 'revision' : 'pendiente',
            icon: getIconForType(doc?.type),
            url: doc?.file_url,
            metadata: doc?.metadata
          })) || []);

          setProcessing(false);
        } catch (err) {
          setError(err?.message || 'Failed to process document');
          setProcessing(false);
        }
      };
      reader?.readAsText(file);
    } catch (err) {
      setError(err?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'aprobado':
        return 'var(--color-success)';
      case 'revision':
        return 'var(--color-warning)';
      case 'pendiente':
        return 'var(--color-muted-foreground)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      aprobado: 'Aprobado',
      revision: 'En Revisión',
      pendiente: 'Pendiente'
    };
    return labels?.[status] || status;
  };

  const handleDownload = (doc) => {
    console.log('Downloading document:', doc?.name);
  };

  const handlePreview = (doc) => {
    setPreviewDocument(doc);
  };

  const closePreview = () => {
    setPreviewDocument(null);
  };

  return (
    <>
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="FolderOpen" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-semibold font-mono">Gestión de Documentos</h2>
          </div>
          <div className="flex items-center gap-2">
            {processing && (
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                Procesando embeddings...
              </span>
            )}
            <label htmlFor="file-upload">
              <Button 
                variant="outline" 
                size="sm" 
                iconName="Upload" 
                iconPosition="left"
                disabled={uploading || processing}
                as="span"
              >
                {uploading ? 'Subiendo...' : 'Subir Documento'}
              </Button>
            </label>
            <input
              id="file-upload"
              type="file"
              accept=".txt,.md,.pdf"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading || processing}
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando documentos...</p>
          </div>
        ) : documents?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="FolderOpen" size={48} className="mx-auto mb-3 opacity-50" />
            <p>No hay documentos. Sube tu primer documento para comenzar.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {documents?.map((doc) => (
              <div
                key={doc?.id}
                className="p-4 bg-background rounded-lg border border-border hover:border-primary/20 hover:shadow-md transition-all duration-250"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="flex items-center justify-center w-12 h-12 rounded-lg flex-shrink-0"
                    style={{ backgroundColor: `${getStatusColor(doc?.status)}15` }}
                  >
                    <Icon name={doc?.icon} size={24} color={getStatusColor(doc?.status)} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-foreground truncate mb-1">
                          {doc?.name}
                        </h3>
                        <p className="text-xs text-muted-foreground caption">{doc?.type}</p>
                      </div>
                      <span
                        className="px-2 py-1 text-xs rounded caption font-medium flex-shrink-0"
                        style={{
                          backgroundColor: `${getStatusColor(doc?.status)}15`,
                          color: getStatusColor(doc?.status)
                        }}
                      >
                        {getStatusLabel(doc?.status)}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-muted-foreground caption mb-3">
                      <span className="flex items-center gap-1">
                        <Icon name="FileType" size={12} />
                        {doc?.format}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="HardDrive" size={12} />
                        {doc?.size}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        {new Date(doc.uploadDate)?.toLocaleDateString('es-ES')}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="xs"
                        iconName="Eye"
                        iconPosition="left"
                        onClick={() => handlePreview(doc)}
                      >
                        Vista Previa
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="Download"
                        iconPosition="left"
                        onClick={() => handleDownload(doc)}
                      >
                        Descargar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {previewDocument && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="modal-backdrop" onClick={closePreview} />
          <div className="modal-content max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-lg"
                  style={{
                    backgroundColor: `${getStatusColor(previewDocument?.status)}15`
                  }}
                >
                  <Icon
                    name={previewDocument?.icon}
                    size={20}
                    color={getStatusColor(previewDocument?.status)}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold font-mono">{previewDocument?.name}</h3>
                  <p className="text-xs text-muted-foreground caption">
                    {previewDocument?.type} • {previewDocument?.version}
                  </p>
                </div>
              </div>
              <button
                onClick={closePreview}
                className="flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-250 focus-ring"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="bg-muted rounded-lg p-8 overflow-y-auto max-h-[60vh]">
              <div className="text-center">
                <Icon name="FileText" size={64} color="var(--color-muted-foreground)" />
                <p className="text-sm text-muted-foreground caption mt-4">
                  Vista previa del documento: {previewDocument?.name}
                </p>
                <p className="text-xs text-muted-foreground caption mt-2">
                  La funcionalidad completa de vista previa se implementará con la integración del
                  backend
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <Button variant="outline" onClick={closePreview}>
                Cerrar
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                onClick={() => handleDownload(previewDocument)}
              >
                Descargar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentManagementSection;